import { type ChangeEvent, type ReactNode, useCallback, useState } from 'react';
import get from 'lodash/get';
import { CFormLabel, CFormInput, CFormText, CFormFeedback } from '@coreui/react';
import type { IFieldComponentProps } from './types';

interface INumberInputProps extends IFieldComponentProps<number | string> {
  description?: ReactNode;
  type?: string;
  className?: string;
  label?: ReactNode;
}

/**
 * The value the input shows: the text being typed while the field is edited (the browser's value of a number input,
 * which is '' for an unfinished number such as "-" or "0,"), otherwise the form's number ('' for a missing one).
 */
const displayValueOf = (typed: string | null, value: unknown): string | number => {
  if (typed !== null) return typed;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return '';
};

const MoneyInput = ({
  field,
  description,
  form,
  type = 'number',
  className,
  label,
}: INumberInputProps) => {
  const { name } = field;
  const { touched, errors, setFieldValue, setFieldTouched } = form;

  const invalid = Boolean(get(touched, name) && get(errors, name));
  // The text while the field is edited. Rendering the parsed number instead set the input to NaN (cleared it) on
  // every unfinished number, so "-0.003" became "003" and a decimal separator vanished.
  const [typed, setTyped] = useState<string | null>(null);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setTyped(e.target.value);
      setFieldValue(name, parseFloat(e.target.value));
    },
    [name, setFieldValue],
  );

  // formik 1's handleBlur took the call without an event (it marked an `undefined` path touched and validated);
  // formik 2's reads the event's target and throws without one. setFieldTouched marks the field and validates.
  const handleOnBlur = useCallback(() => {
    setTyped(null);
    setFieldTouched(name, true);
  }, [name, setFieldTouched]);

  return (
    <div>
      <div className="mb-3">
        <CFormLabel htmlFor={name}>{label}</CFormLabel>
        <CFormInput
          {...field}
          value={displayValueOf(typed, field.value)}
          onBlur={handleOnBlur}
          id={name}
          step="any"
          type={type}
          onChange={handleChange}
          invalid={invalid}
          className={className}
        />
        <CFormText className="help-block">{description}</CFormText>
        {invalid && (
          <CFormFeedback invalid className="help-block">
            {errors[name]}
          </CFormFeedback>
        )}
      </div>
    </div>
  );
};

export default MoneyInput;
